import { useEffect, useState } from "react";

interface FocusableSection<F extends string> {
  id: string;
  focus?: F;
}

/**
 * Tracks which section is in view and returns its focus zone for the page's
 * live side panel. A section without a `focus` (e.g. one that feeds no panel
 * block) retains the previous focus rather than clearing the emphasis to
 * nothing.
 */
export function useFocusObserver<F extends string>(
  sections: FocusableSection<F>[]
): F | undefined {
  const [focus, setFocus] = useState<F | undefined>(sections[0]?.focus);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    const elements = sections
      .map((section) => ({ section, el: document.getElementById(section.id) }))
      .filter((entry): entry is { section: FocusableSection<F>; el: HTMLElement } => entry.el !== null);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const match = elements.find((entry) => entry.el === visible.target);
        if (match?.section.focus) {
          setFocus(match.section.focus);
        }
      },
      { rootMargin: "-15% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    elements.forEach(({ el }) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  return focus;
}
